import api from './client';

export enum CostCenterType {
  DIRECT = 'DIRECT',
  INDIRECT = 'INDIRECT',
  OVERHEAD = 'OVERHEAD',
}

export enum CostCenterStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface CostCenter {
  id: string;
  name: string;
  description?: string;
  type: CostCenterType;
  status: CostCenterStatus;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCostCenterDto {
  name: string;
  description?: string;
  type: CostCenterType;
  status?: CostCenterStatus;
  metadata?: Record<string, any>;
}

export type UpdateCostCenterDto = Partial<CreateCostCenterDto>;

export interface CostCenterSummary {
  costCenter: CostCenter;
  totalAllocated: number;
  allocationCount: number;
}

export const costCentersApi = {
  async getCostCenters(status?: CostCenterStatus): Promise<CostCenter[]> {
    const query = status ? `?status=${status}` : '';
    const response = await api.get<CostCenter[]>(`cost-centers${query}`);
    return response.data;
  },

  async getCostCenter(id: string): Promise<CostCenter> {
    const response = await api.get<CostCenter>(`cost-centers/${id}`);
    return response.data;
  },

  async getCostCenterSummary(id: string): Promise<CostCenterSummary> {
    const response = await api.get<CostCenterSummary>(`cost-centers/${id}/summary`);
    return response.data;
  },

  async createCostCenter(data: CreateCostCenterDto): Promise<CostCenter> {
    const response = await api.post<CostCenter>('cost-centers', data);
    return response.data;
  },

  async seedDefaultCostCenters(): Promise<{ created: number } | null> {
    try {
      const response = await api.post<{ created: number }>('cost-centers/seed-defaults');
      return response.data;
    } catch {
      return null;
    }
  },

  async updateCostCenter(id: string, data: UpdateCostCenterDto): Promise<CostCenter> {
    const response = await api.put<CostCenter>(`cost-centers/${id}`, data);
    return response.data;
  },

  async deleteCostCenter(id: string): Promise<void> {
    await api.delete(`cost-centers/${id}`);
  },
};

export default costCentersApi;
